import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { ChevronDown, X } from "lucide-react";
import { campaignAPI } from "../services/api";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 1400px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  font-family: "Prompt", sans-serif;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 400;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #9ca3af;

  &:hover {
    color: #6b7280;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormField = styled.div``;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #404e67;
  display: block;
  margin-bottom: 0.5rem;
`;

const RequiredStar = styled.span`
  color: #ef4444;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  appearance: none;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const SelectArrow = styled(ChevronDown)`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #111827;
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  color: #111827;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  &:focus {
    outline: none;
    border-color: #00b5b8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DropdownArrow = styled(ChevronDown)`
  flex-shrink: 0;
  color: #111827;
  transition: transform 0.15s ease;
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  z-index: 20;
  padding: 0.75rem;
  max-height: 280px;
  overflow: auto;
`;

const DropdownSearch = styled(Input)`
  margin-bottom: 0.75rem;
`;

const DropdownOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.75rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: #111827;
  font-size: 14px;

  &:hover {
    background: #f3f4f6;
  }
`;

const DropdownEmpty = styled.div`
  padding: 0.75rem;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  margin-top: 2rem;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`;

const SubmitButton = styled.button`
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 3rem;
  font-size: 14px;
  cursor: pointer;
  flex: 1;

  &:hover:not(:disabled) {
    background-color: #009a9d;
  }

  &:disabled {
    background-color: #7dd6d8;
    cursor: not-allowed;
  }
`;

const toBuddhistDate = (dateValue) => {
  if (!dateValue) return "";

  const dateParts = String(dateValue).split("-");
  if (dateParts.length !== 3) return String(dateValue);

  const year = parseInt(dateParts[0], 10);
  if (Number.isNaN(year)) return String(dateValue);

  const buddhistYear = year > 2200 ? year : year + 543;
  return `${buddhistYear}-${dateParts[1]}-${dateParts[2]}`;
};

const toGregorianDate = (dateValue) => {
  if (!dateValue) return "";

  const dateParts = String(dateValue).split("-");
  if (dateParts.length !== 3) return String(dateValue);

  const year = parseInt(dateParts[0], 10);
  if (Number.isNaN(year)) return String(dateValue);

  const gregorianYear = year > 2200 ? year - 543 : year;
  return `${gregorianYear}-${dateParts[1]}-${dateParts[2]}`;
};

const formatDisplayBirthDate = (dateValue) => {
  if (!dateValue) return "";

  const buddhistDate = toBuddhistDate(dateValue);
  const parts = buddhistDate.split("-");
  if (parts.length !== 3) return buddhistDate;

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const EditMemberModal = ({ isOpen, onClose, onSave, memberData }) => {
  const [birthDateDisplay, setBirthDateDisplay] = useState("");
  const [formData, setFormData] = useState({
    prefix: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    gender: "ชาย",
    idCard: "",
    birthDate: "",
    lineUid: "",
    company: "",
    department: "",
    position: "",
    isCompanyAdmin: false,
    username: "",
    password: "",
  });
  const [companiesList, setCompaniesList] = useState([]);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [genderDropdownOpen, setGenderDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const companiesCacheRef = useRef(null);
  const companyDropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (companiesCacheRef.current) {
        setCompaniesList(companiesCacheRef.current);
      } else {
        fetchCompanies();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setCompanySearchTerm("");
      setCompanyDropdownOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target)
      ) {
        setCompanyDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const firstRes = await campaignAPI.getList(1);

      if (firstRes.result && Array.isArray(firstRes.list)) {
        let allCampaigns = [...firstRes.list];
        const pageAmount = firstRes.pageAmount || 1;

        if (pageAmount > 1) {
          const remainingPages = await Promise.all(
            Array.from({ length: pageAmount - 1 }, (_, index) =>
              campaignAPI.getList(index + 2)
            )
          );

          remainingPages.forEach((pageRes) => {
            if (pageRes.result && Array.isArray(pageRes.list)) {
              allCampaigns = allCampaigns.concat(pageRes.list);
            }
          });
        }

        const uniqueCompanies = allCampaigns
          .filter((item) => item.name && item.name !== "-")
          .map((item) => ({
            id: item.id,
            name: item.name,
          }))
          .filter(
            (company, index, self) =>
              index === self.findIndex((c) => c.name === company.name)
          );

        setCompaniesList(uniqueCompanies);
        companiesCacheRef.current = uniqueCompanies;
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
    } finally {
      setLoading(false);
    }
  };

  // fill ข้อมูลทันทีที่เปิด modal ไม่ต้องรอ companies โหลด
  useEffect(() => {
    if (memberData && isOpen) {
      const birthday = memberData.birthday || "";
      const normalizedBirthDate = birthday ? toGregorianDate(birthday) : "";
      setFormData({
        prefix: memberData.nameprefix || "",
        firstName: memberData.name || "",
        lastName: memberData.lastname || "",
        email: memberData.email || "",
        phone: memberData.tel || "",
        gender: memberData.gender === "1" ? "ชาย" : "หญิง",
        idCard: memberData.nid || "",
        birthDate: normalizedBirthDate,
        lineUid: memberData.lineuid || "",
        company: String(memberData.memberCampaign?.[0]?.campaignid || ""),
        department: memberData.position || "", // position field จาก backend = แผนก
        position: memberData.job || "", // job field จาก backend = ตำแหน่งงาน
        isCompanyAdmin: memberData.level === "2",
        username: memberData.username || "",
        password: "",
      });
      setBirthDateDisplay(formatDisplayBirthDate(normalizedBirthDate));
    }
  }, [memberData, isOpen]);

  // อัปเดต company field เมื่อ companies โหลดเสร็จ
  useEffect(() => {
    if (memberData && isOpen && companiesList.length > 0) {
      const matchedCompany = companiesList.find(
        (c) => c.name === memberData.memberCompany
      );
      if (matchedCompany) {
        setFormData((prev) => ({ ...prev, company: String(matchedCompany.id) }));
      }
    }
  }, [memberData, isOpen, companiesList]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const normalizedCompanySearch = companySearchTerm.trim().toLowerCase();
  const filteredCompaniesList = normalizedCompanySearch
    ? companiesList.filter((company) => {
        const companyName = String(company.name || "").toLowerCase();
        const companyId = String(company.id || "").toLowerCase();
        return (
          companyName.includes(normalizedCompanySearch) ||
          companyId.includes(normalizedCompanySearch)
        );
      })
    : companiesList;

  const selectedCompany = companiesList.find(
    (company) => String(company.id) === String(formData.company)
  );

  const visibleCompaniesList =
    selectedCompany &&
    !filteredCompaniesList.some(
      (company) => String(company.id) === String(selectedCompany.id)
    )
      ? [selectedCompany, ...filteredCompaniesList]
      : filteredCompaniesList;

  const handleCompanySelect = (company) => {
    handleInputChange("company", String(company.id));
    setCompanyDropdownOpen(false);
    setCompanySearchTerm("");
  };


  const handleSubmit = async () => {
    if (onSave && memberData) {
      const birthdayForAPI = formData.birthDate
        ? toGregorianDate(formData.birthDate)
        : "2000-01-01";

      const apiData = {
        id: memberData.id,
        username: formData.username || memberData.username,
        password: formData.password || "",
        nameprefix: formData.prefix,
        name: formData.firstName,
        lastname: formData.lastName,
        email: formData.email || "",
        tel: formData.phone || "0",
        nid: formData.idCard || "0",
        birthday: birthdayForAPI,
        gender: formData.gender === "ชาย" ? "1" : "0",
        lineuid: formData.lineUid || "",
        campaignid: formData.company || "0",
        level: formData.isCompanyAdmin ? "2" : "0",
        position: formData.department || "", // แผนก → ส่งเป็น position field
        job: formData.position || "", // ตำแหน่งงาน → ส่งเป็น job field
        mddateadd: memberData.mddateadd || "",
      };

      setSaving(true);
      try {
        await onSave(apiData);
        localStorage.setItem("memberDataUpdated", Date.now().toString());
        window.dispatchEvent(new CustomEvent("memberDataUpdated"));
      } finally {
        setSaving(false);
      }
    } else {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>แก้ไขสมาชิก</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormGrid>
            <FormField>
              <Label>
                คำนำหน้าชื่อ <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.prefix}
                onChange={(e) => handleInputChange("prefix", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                ชื่อ <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                นามสกุล <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>อีเมล</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>
                โทร <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 10) {
                    handleInputChange("phone", value);
                  }
                }}
                maxLength={10}
                placeholder="เบอร์โทร 10 หลัก"
              />
            </FormField>

            <FormField>
              <Label>เพศ</Label>
              <SelectWrapper>
                <Select
                  value={formData.gender}
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                  onFocus={() => setGenderDropdownOpen(true)}
                  onBlur={() => setGenderDropdownOpen(false)}
                >
                  <option value="ชาย">ชาย</option>
                  <option value="หญิง">หญิง</option>
                </Select>
                <SelectArrow
                  size={18}
                  strokeWidth={2.5}
                  style={{
                    transform: `translateY(-50%) rotate(${
                      genderDropdownOpen ? 180 : 0
                    }deg)`,
                  }}
                />
              </SelectWrapper>
            </FormField>

            <FormField>
              <Label>
                เลขบัตรประชาชน/เลขบัตรประกันสังคม/passport สำหรับชาวต่างชาติ
                <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="text"
                value={formData.idCard}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 13) {
                    handleInputChange("idCard", value);
                  }
                }}
                maxLength={13}
                placeholder="เลขบัตรประชาชน 13 หลัก"
              />
            </FormField>

            <FormField>
              <Label>วันเกิด (พ.ศ.)</Label>
              <Input
                type="text"
                value={birthDateDisplay}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^\d/]/g, "");

                  if (value.length > 10) {
                    value = value.substring(0, 10);
                  }

                  setBirthDateDisplay(value);

                  if (!value) {
                    handleInputChange("birthDate", "");
                    return;
                  }

                  const parts = value.split("/");
                  if (
                    parts.length === 3 &&
                    parts[0].length === 2 &&
                    parts[1].length === 2 &&
                    parts[2].length === 4
                  ) {
                    const day = parts[0];
                    const month = parts[1];
                    const year = parts[2];
                    handleInputChange(
                      "birthDate",
                      toGregorianDate(`${year}-${month}-${day}`),
                    );
                  }
                }}
                placeholder="วว/ดด/ปปปป (เช่น 01/01/2545)"
                maxLength={10}
              />
            </FormField>

            <FormField>
              <Label>uid Line application</Label>
              <Input
                type="text"
                value={formData.lineUid}
                onChange={(e) => handleInputChange("lineUid", e.target.value)}
              />
            </FormField>

            <FormField>
              <Label>บริษัท</Label>
              <DropdownWrapper ref={companyDropdownRef}>
                <DropdownButton
                  type="button"
                  onClick={() =>
                    !loading && setCompanyDropdownOpen((prev) => !prev)
                  }
                  disabled={loading}
                >
                  <span>
                    {loading
                      ? "กำลังโหลด..."
                      : selectedCompany?.name || "เลือกบริษัท"}
                  </span>
                  <DropdownArrow
                    size={18}
                    strokeWidth={2.5}
                    style={{
                      transform: `rotate(${companyDropdownOpen ? 180 : 0}deg)`,
                    }}
                  />
                </DropdownButton>

                {companyDropdownOpen && !loading && (
                  <DropdownPanel>
                    <DropdownSearch
                      type="text"
                      value={companySearchTerm}
                      onChange={(e) => setCompanySearchTerm(e.target.value)}
                      placeholder="ค้นหาบริษัท"
                      onClick={(e) => e.stopPropagation()}
                    />

                    {visibleCompaniesList.length === 0 ? (
                      <DropdownEmpty>ไม่พบบริษัทที่ค้นหา</DropdownEmpty>
                    ) : (
                      visibleCompaniesList.map((company) => (
                        <DropdownOption
                          key={company.id}
                          type="button"
                          onClick={() => handleCompanySelect(company)}
                        >
                          <span>{company.name}</span>
                        </DropdownOption>
                      ))
                    )}
                  </DropdownPanel>
                )}
              </DropdownWrapper>
            </FormField>

            <FormField>
              <Label>&nbsp;</Label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "0.5rem",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#374151",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.isCompanyAdmin}
                    onChange={(e) =>
                      handleInputChange("isCompanyAdmin", e.target.checked)
                    }
                    style={{
                      width: "1rem",
                      height: "1rem",
                      cursor: "pointer",
                      accentColor: "#00B5B8",
                    }}
                  />
                  admin บริษัท
                </label>
              </div>
            </FormField>

            <FormField>
              <Label>
                ชื่อผู้ใช้ (Username) <RequiredStar>*</RequiredStar>
              </Label>
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <span
                  style={{
                    padding: "0.75rem",
                    background: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRight: "none",
                    borderRadius: "6px 0 0 6px",
                    fontSize: "14px",
                    color: "#6b7280",
                    whiteSpace: "nowrap",
                  }}
                >
                  MFU
                </span>
                <Input
                  type="text"
                  value={formData.username || ""}
                  onChange={(e) => handleInputChange("username", e.target.value)}
                  style={{ borderRadius: "0 6px 6px 0" }}
                />
              </div>
            </FormField>

            <FormField>
              <Label>แผนก</Label>
              <Input
                type="text"
                value={formData.department}
                onChange={(e) =>
                  handleInputChange("department", e.target.value)
                }
              />
            </FormField>

            <FormField></FormField>

            <FormField>
              <Label>
                รหัสผ่าน (Password) <RequiredStar>*</RequiredStar>
              </Label>
              <Input
                type="password"
                value={formData.password || ""}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="ปล่อยว่างถ้าไม่ต้องการเปลี่ยน"
              />
            </FormField>

            <FormField>
              <Label>ตำแหน่งงาน</Label>
              <Input
                type="text"
                value={formData.position}
                onChange={(e) => handleInputChange("position", e.target.value)}
              />
            </FormField>

            <FormField></FormField>
            <FormField></FormField>
          </FormGrid>

          <ButtonContainer>
            <CancelButton onClick={onClose}>ยกเลิก</CancelButton>
            <SubmitButton onClick={handleSubmit} disabled={saving}>
              {saving ? "กำลังบันทึก..." : "บันทึก"}
            </SubmitButton>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default EditMemberModal;
